import React, { useState } from 'react';
import { StatusBlock } from '../../common/status-block';
import moment from 'moment';
import { CleanDeliveryOrder } from '../../../export-types/cleaned-types';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { RetailAttentionStatus } from './retail-attention-status';
import { ChildStatusBlock } from '../../common/child-status-block';

export type DeliveryEventStatus =
  | 'sentToRussia'
  | 'receivedInRussia'
  | 'sentToRecipient'
  | 'delivered'
  | 'informationAccepted'
  | 'attention';

export type DeliveryEvent = {
  status: DeliveryEventStatus;
  code: string | null;
  text: string | null;
  date: string;
};

type DeliveryStatusTableProps = {
  order: CleanDeliveryOrder;
  events: DeliveryEvent[];
};

const useChildEvents = (
  events: DeliveryEvent[],
  statusKey: DeliveryEventStatus,
) => {
  return React.useMemo(() => {
    return events
      .filter((event) => event.status === statusKey)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [events, statusKey]);
};

export const RetailDeliveryOrderStatusTable: React.FC<
  DeliveryStatusTableProps
> = ({ order, events }) => {
  const informationAcceptedEvents = useChildEvents(
    events,
    'informationAccepted',
  );
  const sentToRussiaEvents = useChildEvents(events, 'sentToRussia');
  const receivedInRussiaEvents = useChildEvents(events, 'receivedInRussia');
  const sentToRecipientEvents = useChildEvents(events, 'sentToRecipient');
  const deliveredEvents = useChildEvents(events, 'delivered');

  const [openStatuses, setOpenStatuses] = useState<Record<string, boolean>>({});

  const toggleStatus = (key: string) => {
    setOpenStatuses((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderSubStatuses = (key: string, childEvents: DeliveryEvent[]) => {
    if (!childEvents.length || !openStatuses[key]) return null;

    return (
      <div className="mt-2">
        {childEvents.map((event, idx) => {
          const isLast = idx === childEvents.length - 1;

          return (
            <ChildStatusBlock
              key={idx}
              title={`${event.code}${event.text ? `: ${event.text}` : ''}`}
              subtitle={moment(event.date).format('DD.MM.YYYY, HH:mm')}
              status="done"
              connector={!isLast}
            />
          );
        })}
      </div>
    );
  };

  const Arrow = (key: string, eventsArr: DeliveryEvent[]) =>
    eventsArr.length > 0 ? (
      <div
        className="ms-2 p-1 cursor-pointer"
        onClick={() => toggleStatus(key)}
      >
        <i
          className={
            openStatuses[key]
              ? 'bi bi-caret-up-fill color-green'
              : 'bi bi-caret-down-fill color-green'
          }
        />
      </div>
    ) : null;

  const deliveryDate = React.useMemo(() => {
    if (!order) return undefined;

    if (order.status !== 'sentToRussia' && order.receiveRfDate) {
      return moment(order.receiveRfDate).format('DD.MM.YYYY, HH:mm');
    }

    if (
      order.status === 'sentToRussia' &&
      !order.receiveRfDate &&
      order.deliveryMethod
    ) {
      const start = moment(order.sentDate)
        .add(order.deliveryMethod.deliveryPeriodFrom, 'd')
        .format('DD.MM.YYYY');

      const end = moment(order.sentDate)
        .add(order.deliveryMethod.deliveryPeriodTo, 'd')
        .format('DD.MM.YYYY');

      return `Получим ${start} - ${end}`;
    }

    return undefined;
  }, [order]);

  return (
    <ul className="d-flex flex-column p-0 mb-0">
      {order.retailOrder?.isAttention && (
        <RetailAttentionStatus retailOrder={order.retailOrder} />
      )}

      <StatusBlock
        key="informationAccepted"
        title={
          <div className="d-flex align-items-center">
            <>
              {order.status === 'retail' || order.status === 'assembly' ? (
                <>
                  <FormattedMessage
                    defaultMessage="На комплектации"
                    id="delivery.status-label.onPicking"
                    description="Статус доставки"
                  />

                  {order.transportation?.type === 'combined' && (
                    <span>
                      ,{' '}
                      <Link to={`/transportations/${order.transportation.id}`}>
                        {order.transportation.name}
                      </Link>
                    </span>
                  )}
                </>
              ) : (
                <FormattedMessage
                  defaultMessage="Упакован и отправлен"
                  id="delivery.status-label.packedAndShipped"
                  description="Статус доставки"
                />
              )}
            </>
            {Arrow('informationAccepted', informationAcceptedEvents)}
          </div>
        }
        subtitle={
          order.sentDate &&
          (order.supplier?.location
            ? `${order.supplier.location.displayName}, ${moment(order.sentDate).format('DD.MM.YYYY, HH:mm')}`
            : moment(order.sentDate).format('DD.MM.YYYY, HH:mm'))
        }
        status={
          order.status === 'retail' || order.status === 'assembly'
            ? 'active'
            : order.sentDate
              ? 'done'
              : undefined
        }
        connector={true}
        subStatuses={renderSubStatuses(
          'informationAccepted',
          informationAcceptedEvents,
        )}
      />

      <StatusBlock
        key="sentToRussia"
        title={
          <div className="d-flex align-items-center">
            <>
              <FormattedMessage
                defaultMessage="В пути на склад в России"
                id="delivery.status-label.InTransitToAWarehouseInRussia"
                description="Статус доставки"
              />
              {order.status === 'sentToRussia' &&
                order.transportation &&
                !order.transportation.receivedAt && (
                  <span>
                    ,{' '}
                    <Link to={`/transportations/${order.transportation.id}`}>
                      {order.transportation.name}
                    </Link>
                  </span>
                )}
            </>
            {Arrow('sentToRussia', sentToRussiaEvents)}
          </div>
        }
        subtitle={
          order.sentDate && moment(order.sentDate).format('DD.MM.YYYY, HH:mm')
        }
        status={
          order.status === 'sentToRussia'
            ? 'active'
            : order.receiveRfDate
              ? 'done'
              : undefined
        }
        connector={true}
        subStatuses={renderSubStatuses('sentToRussia', sentToRussiaEvents)}
      />

      <StatusBlock
        key="receivedInRussia"
        title={
          <div className="d-flex align-items-center">
            <>
              <FormattedMessage
                defaultMessage="Прибыл на склад в России"
                id="delivery.status-label.arrivedAtWarehouseInRussia"
                description="Статус доставки"
              />
            </>
            {Arrow('receivedInRussia', receivedInRussiaEvents)}
          </div>
        }
        subtitle={deliveryDate}
        status={order.receiveRfDate ? 'done' : undefined}
        connector={true}
        subStatuses={renderSubStatuses(
          'receivedInRussia',
          receivedInRussiaEvents,
        )}
      />

      <StatusBlock
        key="sentToRecipient"
        title={
          <div className="d-flex align-items-center">
            <>
              {order.status === 'receivedInRussia' ? (
                <>
                  <FormattedMessage
                    defaultMessage="Ожидает передачу партнёру последней мили"
                    id="delivery.status-label.awaitingHandoverToLastMilePartner"
                    description="Статус доставки"
                  />
                </>
              ) : (
                <FormattedMessage
                  defaultMessage="Передан партнёру последней мили"
                  id="delivery.status-label.transferredToLastMilePartnery"
                  description="Статус доставки"
                />
              )}
            </>
            {Arrow('sentToRecipient', sentToRecipientEvents)}
          </div>
        }
        subtitle={
          order.sentToRecipientDate &&
          moment(order.sentToRecipientDate).format('DD.MM.YYYY, HH:mm')
        }
        status={
          order.status === 'receivedInRussia'
            ? 'active'
            : order.sentToRecipientDate
              ? 'done'
              : undefined
        }
        connector={true}
        subStatuses={renderSubStatuses(
          'sentToRecipient',
          sentToRecipientEvents,
        )}
      />

      <StatusBlock
        key="delivered"
        title={
          <div className="d-flex align-items-center">
            <>
              {order.status === 'sentToRecipient' ? (
                <FormattedMessage
                  defaultMessage="Ожидает вручения"
                  id="delivery.status-label.awaitingReceipt"
                  description="Статус доставки"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Получен клиентом"
                  id="delivery.status-label.receiptedByTheClient"
                  description="Статус доставки"
                />
              )}
            </>
            {Arrow('delivered', deliveredEvents)}
          </div>
        }
        subtitle={
          order.deliveredDate &&
          moment(order.deliveredDate).format('DD.MM.YYYY, HH:mm')
        }
        status={
          order.status === 'sentToRecipient'
            ? 'active'
            : order.deliveredDate
              ? 'done'
              : undefined
        }
        connector={false}
        subStatuses={renderSubStatuses('delivered', deliveredEvents)}
      />
    </ul>
  );
};
